import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Spline from "@splinetool/react-spline";

// Note for Next.js environments:
// If this component is ported to Next.js, import Spline from "@splinetool/react-spline/next" for SSR/Image optimizations.

const HomeSectionPage = () => {
  const [loading, setLoading] = useState(true);

  return (
    <>
      {/* ================= ADVANCED HELMET SEO ================= */}
      <Helmet>
        <title>
          Novarsis Technology | Leading IT Services & Software Development
          Company
        </title>

        <meta
          name="description"
          content="Novarsis Technology provides scalable software development, IT services, web solutions, and digital innovation to help modern businesses grow faster."
        />

        <meta
          name="keywords"
          content="Novarsis Technology, IT services company, software development company, web development, digital solutions India"
        />

        {/* ✅ Updated Canonical */}
        <link rel="canonical" href="https://novarsistech.com/" />

        <meta name="robots" content="index, follow" />

        {/* ✅ Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Novarsis Technology | Leading IT Services & Software Development"
        />
        <meta
          property="og:description"
          content="Building scalable, secure, and future-ready digital solutions for modern businesses."
        />
        <meta property="og:url" content="https://novarsistech.com/" />
        <meta
          property="og:image"
          content="https://novarsistech.com/Images/novarsis-og-image.jpg"
        />

        {/* ✅ Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Novarsis Technology | IT Services & Software Experts"
        />
        <meta
          name="twitter:description"
          content="Innovative IT services, web development, and digital solutions for growing businesses."
        />
        <meta
          name="twitter:image"
          content="https://novarsistech.com/Images/novarsis-og-image.jpg"
        />

        {/* ✅🔥 ORGANIZATION SCHEMA (VERY IMPORTANT FOR BRAND SEO) */}
        <script type="application/ld+json">
          {`
      {
        "@context":"https://schema.org",
        "@type":"Organization",
        "name":"Novarsis Technology",
        "url":"https://novarsistech.com",
        "logo":"https://novarsistech.com/logo.png",
        "sameAs":[
          "https://www.facebook.com/",
          "https://www.linkedin.com/"
        ]
      }
    `}
        </script>
      </Helmet>

      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes spline-spin-cw {
          0% { transform: translate(-50%, -50%) rotateX(68deg) rotateY(-25deg) rotateZ(0deg); }
          100% { transform: translate(-50%, -50%) rotateX(68deg) rotateY(-25deg) rotateZ(360deg); }
        }
        @keyframes spline-spin-ccw {
          0% { transform: translate(-50%, -50%) rotateX(-58deg) rotateY(35deg) rotateZ(360deg); }
          100% { transform: translate(-50%, -50%) rotateX(-58deg) rotateY(35deg) rotateZ(0deg); }
        }
        @keyframes spin-globe-v {
          0% { transform: translate(-50%, -50%) rotateY(0deg) rotateX(15deg); }
          100% { transform: translate(-50%, -50%) rotateY(360deg) rotateX(15deg); }
        }
        @keyframes spin-globe-h {
          0% { transform: translate(-50%, -50%) rotateX(80deg) rotateZ(0deg); }
          100% { transform: translate(-50%, -50%) rotateX(80deg) rotateZ(360deg); }
        }
        @keyframes float-globe {
          0%, 100% { transform: translate(-50%, -50%) translateY(0px) scale(1); }
          50% { transform: translate(-50%, -50%) translateY(-12px) scale(1.01); }
        }
        @keyframes float-p-1 {
          0%, 100% { transform: translate(0, 0); opacity: 0.3; }
          50% { transform: translate(15px, -35px); opacity: 0.9; }
        }
        @keyframes float-p-2 {
          0%, 100% { transform: translate(0, 0); opacity: 0.2; }
          50% { transform: translate(-25px, -15px); opacity: 0.8; }
        }
        @keyframes float-p-3 {
          0%, 100% { transform: translate(0, 0); opacity: 0.4; }
          50% { transform: translate(20px, 25px); opacity: 0.9; }
        }
        @keyframes float-p-4 {
          0%, 100% { transform: translate(0, 0); opacity: 0.2; }
          50% { transform: translate(-20px, 30px); opacity: 0.7; }
        }
        @keyframes light-streak {
          0% { transform: translateX(-100%); opacity: 0; }
          10% { opacity: 0.3; }
          90% { opacity: 0.3; }
          100% { transform: translateX(100vw); opacity: 0; }
        }
        /* Hide Spline Watermark elements */
        #logo,
        a[href*="spline.design"],
        a[href*="spline"],
        .spline-watermark,
        [class*="spline-watermark"],
        div[style*="branding"],
        a[target="_blank"][style*="position: absolute"] {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
        /* Fix robot hand cutoff at 1024px */
        @media (max-width: 1024px) {
          .robot-canvas-fix {
            top: -15% !important;
            left: -10% !important;
            width: 140% !important;
            height: 130% !important;
          }
        }
        /* Show buttons side-by-side at 425px and above */
        @media (min-width: 425px) {
          .cta-buttons-row {
            flex-direction: row !important;
          }
        }
        /* Adjustments for 768px */
        @media (max-width: 768px) {
          .hero-heading {
            font-size: 1.5rem !important;
          }
          .hero-description {
            font-size: 0.875rem !important;
          }
          .hero-container {
            gap: 0 !important;
          }
          .hero-button {
            padding: 0.5rem 1rem !important;
            font-size: 0.875rem !important;
          }
          .hero-text-column {
            padding-left: 2rem !important;
          }
        }
        /* Adjustments for 769px to 1023px */
        @media (min-width: 769px) and (max-width: 1023px) {
          .hero-heading {
            font-size: 2rem !important;
          }
          .hero-description {
            font-size: 1rem !important;
          }
          .hero-container {
            gap: 1rem !important;
          }
          .hero-text-column {
            text-align: center !important;
            align-items: center !important;
          }
          .hero-button {
            padding: 0.6rem 1.2rem !important;
            font-size: 0.8rem !important;
          }
        }
        /* Adjustments for 425px */
        @media (max-width: 425px) {
          .hero-button {
            padding: 0.5rem 1rem !important;
            font-size: 0.7rem !important;
          }
        }
        /* Adjustments for 1024px to 1200px */
        @media (min-width: 1024px) and (max-width: 1200px) {
          .hero-heading {
            font-size: 2.5rem !important;
          }
          .hero-description {
            font-size: 1.1rem !important;
          }
          .hero-button {
            padding: 0.7rem 1.4rem !important;
            font-size: 0.9rem !important;
          }
          .hero-robot-column {
            width: 55% !important;
          }
        }
      `}} />

      <section className="relative min-h-[85vh] w-full flex items-center justify-center" style={{ overflow: 'clip' }}>

        {/* ── BACKGROUND VIDEO LAYER ── */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover pointer-events-none z-0"
          style={{ opacity: 1, filter: 'blur(3px)', transform: 'scale(1.05)' }}
        >
          <source src="/Images/video-forward.mp4" type="video/mp4" />
        </video>
        {/* Dark overlay so text and robot stay clearly readable over blurred video */}
        <div className="absolute inset-0 bg-[#0b1120]/55 pointer-events-none z-0" />

        {/* LIGHT STREAK BACKGROUND EFFECT */}
        <div
          className="absolute top-1/3 left-0 w-[200px] h-[1px] bg-gradient-to-r from-transparent via-[#00ff66]/15 to-transparent pointer-events-none z-0"
          style={{ animation: 'light-streak 14s linear infinite' }}
        />

        {/* FUTURISTIC SMOOTH LOADING PLACEHOLDER */}
        {loading && (
          <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-[#0b1120] transition-opacity duration-500">
            <div className="relative flex items-center justify-center">
              {/* Outer pulsing glow */}
              <div className="absolute w-24 h-24 rounded-full border border-[#00ff66]/30 animate-ping opacity-75" />
              {/* Spinning loading ring */}
              <div className="w-16 h-16 border-4 border-[#00ff66]/20 border-t-[#00ff66] rounded-full animate-spin" />
            </div>
            <p className="mt-6 text-gray-400 text-sm font-semibold tracking-widest uppercase animate-pulse">
              Loading 3D Experience...
            </p>
          </div>
        )}

        {/* MAIN TWO-COLUMN CONTAINER */}
        <div
          className="
            hero-container
            relative z-10
            flex flex-col lg:flex-row
            items-center
            justify-between
            w-full
            max-w-7xl
            mx-auto
            px-6 sm:px-8 lg:px-12
            pt-12 pb-12 lg:py-0
            min-h-[85vh]
            gap-8
            pointer-events-none
          "
        >
          {/* LEFT COLUMN: TEXT CONTENT */}
          <div
            className="
              hero-text-column
              w-full lg:w-[48%]
              flex flex-col items-start
              text-left
              pointer-events-auto
              order-1
            "
          >
            {/* SaaS Subheading Badge */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-green-500/10 border border-green-500/20 rounded-full text-xs font-semibold text-[#00ff00] mb-6 tracking-wide backdrop-blur-sm animate-pulse">
              <span className="w-2 h-2 rounded-full bg-[#00ff00] inline-block animate-ping" />
              <span>✨ Next-Gen Software Solutions</span>
            </div>

            {/* Title / Heading */}
            <h1
              className="
                hero-heading
                text-2xl
                sm:text-3xl
                md:text-4xl
                lg:text-5xl
                font-extrabold
                text-white
                leading-[1.1]
                mb-6
                tracking-tight
              "
            >
              Leading IT Services <br />
              <span className="bg-gradient-to-r from-green-400 via-green-500 to-emerald-600 bg-clip-text text-transparent">
                & Software Development
              </span>
            </h1>

            {/* Description */}
            <p
              className="
                hero-description
                text-gray-400
                text-sm
                sm:text-base
                md:text-lg
                leading-relaxed
                mb-8
                max-w-xl
              "
            >
              NovarsisTech builds scalable, secure, and future-ready digital
              solutions to help modern businesses grow faster.
            </p>

            {/* CTA Buttons */}
            <div className="cta-buttons-row flex flex-row items-center gap-4 w-full sm:w-auto">
              <Link to="/about" className="inline-block">
                <button
                  className="
                    hero-button
                    w-full sm:w-auto
                    bg-[#008300]
                    hover:bg-green-600
                    px-8 py-4
                    rounded-full
                    text-white
                    text-base
                    font-bold
                    transition
                    duration-300
                    hover:scale-[1.02]
                    shadow-[0_0_30px_rgba(0,131,0,0.35)]
                    flex items-center justify-center gap-2
                  "
                >
                  Start Your Journey <span className="text-xl">&rarr;</span>
                </button>
              </Link>
              <a href="#services-section" className="inline-block">
                <button
                  className="
                    hero-button
                    w-full sm:w-auto
                    bg-white/5
                    hover:bg-white/10
                    border border-white/10
                    px-8 py-4
                    rounded-full
                    text-white
                    text-base
                    font-bold
                    transition
                    duration-300
                    hover:scale-[1.02]
                    flex items-center justify-center gap-2
                  "
                >
                  Explore Services <span className="text-xl">&rarr;</span>
                </button>
              </a>
            </div>
          </div>

          {/* RIGHT COLUMN: INTERACTIVE SPLINE 3D ROBOT SCENE WITH HOLOGRAPHIC AI EFFECTS */}
          <div
            className="
              hero-robot-column
              w-full lg:w-[50%]
              h-[320px] sm:h-[400px] lg:h-[580px]
              relative
              pointer-events-auto
              order-2
              flex items-center justify-center
              overflow-visible
            "
          >
            {/* 1. Behind the Robot: Soft green glowing energy fields */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] sm:w-[400px] h-[280px] sm:h-[400px] bg-[#00ff66]/10 rounded-full blur-[80px] sm:blur-[110px] pointer-events-none z-0 animate-pulse" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[180px] sm:w-[250px] h-[180px] sm:h-[250px] bg-emerald-500/10 rounded-full blur-[50px] sm:blur-[70px] pointer-events-none z-0" />

            {/* 3. Tilted Neon Green Orbital Energy Ring */}
            <div
              className="absolute top-1/2 left-1/2 w-[390px] sm:w-[500px] md:w-[540px] h-[390px] sm:h-[500px] md:h-[540px] rounded-full border-[3px] border-[#00ff66] shadow-[0_0_35px_#00ff66,inset_0_0_20px_rgba(0,255,102,0.4)] pointer-events-none z-0 opacity-90"
              style={{
                animation: 'spline-spin-cw 18s linear infinite',
                filter: 'drop-shadow(0 0 8px #00ff66)'
              }}
            />
            {/* Concentric faint secondary ring to add visual trail depth */}
            <div
              className="absolute top-1/2 left-1/2 w-[350px] sm:w-[460px] md:w-[500px] h-[350px] sm:h-[460px] md:h-[500px] rounded-full border border-dashed border-[#00ff66]/40 pointer-events-none z-0"
              style={{
                animation: 'spline-spin-ccw 25s linear infinite'
              }}
            />

            {/* 4. Floating particle dots */}
            <div className="absolute top-[20%] left-[20%] w-2 h-2 bg-[#00ff66] rounded-full shadow-[0_0_8px_#00ff66] pointer-events-none z-10" style={{ animation: 'float-p-1 8s ease-in-out infinite' }} />
            <div className="absolute bottom-[25%] left-[15%] w-1.5 h-1.5 bg-[#00ff66] rounded-full shadow-[0_0_8px_#00ff66] pointer-events-none z-10" style={{ animation: 'float-p-2 10s ease-in-out infinite' }} />
            <div className="absolute top-[25%] right-[15%] w-2 h-2 bg-[#00ff66] rounded-full shadow-[0_0_8px_#00ff66] pointer-events-none z-10" style={{ animation: 'float-p-3 9s ease-in-out infinite' }} />
            <div className="absolute bottom-[20%] right-[20%] w-1.5 h-1.5 bg-[#00ff66]/80 rounded-full shadow-[0_0_6px_#00ff66] pointer-events-none z-10" style={{ animation: 'float-p-4 11s ease-in-out infinite' }} />

            {/* Additional tiny glowing particles around the globe */}
            <div className="absolute top-[15%] left-[25%] w-1 h-1 bg-[#00ff66]/60 rounded-full shadow-[0_0_4px_#00ff66] pointer-events-none z-10 animate-pulse" style={{ animation: 'float-p-1 12s ease-in-out infinite' }} />
            <div className="absolute bottom-[18%] left-[20%] w-1 h-1 bg-[#00ff66]/50 rounded-full shadow-[0_0_4px_#00ff66] pointer-events-none z-10" style={{ animation: 'float-p-2 14s ease-in-out infinite' }} />
            <div className="absolute top-[35%] right-[22%] w-1 h-1 bg-[#00ff66]/70 rounded-full shadow-[0_0_4px_#00ff66] pointer-events-none z-10 animate-pulse" style={{ animation: 'float-p-3 10s ease-in-out infinite' }} />
            <div className="absolute bottom-[30%] right-[15%] w-1 h-1 bg-[#00ff66]/40 rounded-full shadow-[0_0_4px_#00ff66] pointer-events-none z-10" style={{ animation: 'float-p-4 13s ease-in-out infinite' }} />

            {/* 5. ACTUAL SPLINE CANVAS (Oversized canvas for full robot visibility - no hand clipping) */}
            <div
              className="z-10 overflow-visible robot-canvas-fix"
              style={{
                position: 'absolute',
                top: '-10%',
                left: '-5%',
                width: '130%',
                height: '120%',
                pointerEvents: 'auto'
              }}
            >
              <Spline
                scene="https://prod.spline.design/McHm6ZTwHtwB-4cF/scene.splinecode"
                onLoad={() => setLoading(false)}
                style={{ width: '100%', height: '100%' }}
              />
              {/* DIRECT CANVAS WATERMARK COVER MASK (Positioned relative to shifted canvas container) */}
              <div
                className="absolute bottom-3 right-4 w-[180px] h-[48px] pointer-events-none z-[99999]"
                style={{
                  background: 'linear-gradient(to top, #0b1120 50%, rgba(11,17,32,0.9) 80%, rgba(11,17,32,0) 100%)',
                  filter: 'blur(3px)'
                }}
              />
              {/* A clean secondary solid mask for absolute coverage */}
              <div className="absolute bottom-3 right-4 w-[160px] h-[36px] bg-[#0b1120] rounded-full pointer-events-none z-[99998]" />
            </div>
          </div>
        </div>

        {/* SOLID BLENDED COVER FOR THE BOTTOM-RIGHT SPLINE WATERMARK */}
        <div
          className="absolute bottom-0 right-0 w-[240px] h-[95px] pointer-events-none z-[99999]"
          style={{
            background: 'linear-gradient(to top, #0b1120 40%, rgba(11,17,32,0.9) 70%, rgba(11,17,32,0) 100%)',
            filter: 'blur(8px)'
          }}
        />
        {/* Subtle green ambient glow behind the mask to integrate it with the futuristic theme */}
        <div className="absolute bottom-0 right-0 w-[200px] h-[60px] bg-[#00ff66]/10 rounded-full blur-[40px] pointer-events-none z-[99998]" />
      </section>
    </>
  );
};

export default HomeSectionPage;

